import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { X } from 'lucide-react';
import { auth } from '../lib/firebase';
import { useAuthStore } from '../lib/authStore';

export default function AuthModal({ open, onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const login = useAuthStore(s => s.login);

  const handleGoogle = async () => {
    setLoading(true);
    setError('');
    try {
      const result = await signInWithPopup(auth, new GoogleAuthProvider());
      await login(result.user);
      onSuccess?.();
      onClose();
    } catch (_e) {
      setError('Sign-in failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            key="backdrop"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={onClose}
            style={{
              position: 'fixed', inset: 0, zIndex: 200,
              background: 'rgba(0,0,0,0.18)',
              backdropFilter: 'blur(4px)',
            }}
          />

          {/* Centering shell — flex does the positioning, motion does the animation */}
          <div
            key="panel-shell"
            style={{
              position: 'fixed', inset: 0, zIndex: 201,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              pointerEvents: 'none',
            }}
          >
          <motion.div
            initial={{ opacity: 0, y: 16, scale: 0.97 }}
            animate={{ opacity: 1, y: 0,  scale: 1 }}
            exit={{ opacity: 0,  y: 8,   scale: 0.97 }}
            transition={{ duration: 0.22 }}
            style={{
              pointerEvents: 'auto',
              width: 360,
              background: '#fff',
              border: '1px solid #E4E4E7',
              borderRadius: 20,
              padding: '36px 32px 28px',
              boxShadow: '0 20px 60px rgba(0,0,0,0.12)',
              position: 'relative',
            }}
          >
            <button
              onClick={onClose}
              style={{
                position: 'absolute', top: 16, right: 16,
                background: 'none', border: 'none', cursor: 'pointer',
                color: '#A1A1AA', display: 'flex',
              }}
            >
              <X size={16} />
            </button>

            <img src="/assets/logo.svg" alt="eonik"
              style={{ height: 18, marginBottom: 24, display: 'block' }} />

            <h2 style={{
              fontSize: 20, fontWeight: 700, color: '#09090B',
              letterSpacing: '-0.03em', margin: '0 0 6px',
            }}>
              Sign in to continue
            </h2>
            <p style={{
              fontSize: 13, color: '#71717A', margin: '0 0 28px',
              lineHeight: 1.5,
            }}>
              Sign in to compare two ads and get a clear winner.
            </p>

            <button
              onClick={handleGoogle}
              disabled={loading}
              style={{
                width: '100%', padding: '11px 16px',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                border: '1px solid #E4E4E7',
                borderRadius: 10, cursor: loading ? 'not-allowed' : 'pointer',
                background: loading ? '#F4F4F5' : '#fff',
                fontSize: 14, fontWeight: 500, color: '#09090B',
                transition: 'background 0.15s, border-color 0.15s',
                fontFamily: 'Inter, sans-serif',
              }}
              onMouseEnter={e => { if (!loading) e.currentTarget.style.background = '#FAFAFA'; }}
              onMouseLeave={e => { if (!loading) e.currentTarget.style.background = '#fff'; }}
            >
              {loading ? (
                <div style={{
                  width: 16, height: 16,
                  border: '2px solid #D4D4D8',
                  borderTopColor: '#18181B',
                  borderRadius: '50%',
                  animation: 'spin 0.8s linear infinite',
                }} />
              ) : (
                <svg width="18" height="18" viewBox="0 0 18 18">
                  <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
                  <path d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z" fill="#34A853"/>
                  <path d="M3.964 10.707c-.18-.54-.282-1.117-.282-1.707s.102-1.167.282-1.707V4.961H.957C.347 6.175 0 7.55 0 9s.348 2.825.957 4.039l3.007-2.332z" fill="#FBBC05"/>
                  <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.961L3.964 7.293C4.672 5.166 6.656 3.58 9 3.58z" fill="#EA4335"/>
                </svg>
              )}
              {loading ? 'Signing in…' : 'Continue with Google'}
            </button>

            {error && (
              <p style={{ fontSize: 12, color: '#EF4444', marginTop: 12, textAlign: 'center' }}>
                {error}
              </p>
            )}

            <p style={{
              fontSize: 11, color: '#A1A1AA', marginTop: 20,
              textAlign: 'center', lineHeight: 1.5,
            }}>
              By continuing, you agree to eonik terms.
            </p>
          </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}
